import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { api } from '../../utils/api';
import { orderAPI } from '../../services/marketplaceAPI';
import PaymentGateway from '../../components/payments/PaymentGateway';
import './OxygenCylinderPayment.css';

const OxygenCylinderPayment = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [showPaymentGateway, setShowPaymentGateway] = useState(false);
  const [orderData, setOrderData] = useState(null);
  const [orderId, setOrderId] = useState(null);

  useEffect(() => {
    const { orderData: data, orderId: id } = location.state || {};
    if (!data || !id) {
      navigate('/emergency/oxygen-cylinder');
    } else {
      setOrderData(data);
      setOrderId(id);
      setShowPaymentGateway(true);
    }
  }, [location.state, navigate]);

  const handlePaymentSuccess = async (paymentData) => {
    try {
      console.log('Payment successful, creating order (marketplace approach):', paymentData);
      
      // Transform deliveryAddress to shippingAddress format
      const shippingAddress = orderData.deliveryAddress ? {
        street: orderData.deliveryAddress.street || orderData.deliveryAddress.address || '',
        city: orderData.deliveryAddress.city || '',
        state: orderData.deliveryAddress.state || '',
        zipCode: orderData.deliveryAddress.zipCode || orderData.deliveryAddress.postalCode || '',
        country: orderData.deliveryAddress.country || 'Bangladesh',
        phone: orderData.deliveryAddress.phone || orderData.customerPhone || ''
      } : null;

      if (!shippingAddress || !shippingAddress.street || !shippingAddress.city) {
        alert('Delivery address is missing. Please go back and provide your address.');
        return;
      }
      
      // Use the EXACT same approach as marketplace - create order after payment
      const orderPayload = {
        paymentType: 'oxygen_cylinder',
        amount: paymentData.amount,
        originalAmount: paymentData.amount,
        payment: {
          transactionId: paymentData.transactionId || paymentData.orderId,
          paymentMethod: paymentData.paymentMethod
        },
        shippingAddress: shippingAddress,
        notes: orderData.notes || '',
        items: [{
          type: 'oxygen_cylinder',
          productId: orderData.planId || 'oxygen-cylinder',
          name: orderData.plan?.name || 'Oxygen Cylinder Subscription',
          description: `${orderData.plan?.name || 'Oxygen Cylinder'} - ${orderData.delivery?.name || 'Delivery'}`,
          category: 'medicine',
          quantity: 1,
          price: orderData.totalAmount || paymentData.amount,
          total: orderData.totalAmount || paymentData.amount
        }],
        deliveryInfo: {
          address: shippingAddress.street,
          city: shippingAddress.city,
          state: shippingAddress.state,
          postalCode: shippingAddress.zipCode,
          zipCode: shippingAddress.zipCode,
          country: shippingAddress.country,
          phone: shippingAddress.phone,
          type: 'emergency_delivery'
        },
        customerInfo: {
          name: 'Emergency Patient',
          phone: shippingAddress.phone,
          email: ''
        }
      };
      
      console.log('Creating order after payment (marketplace approach):', orderPayload);
      const response = await orderAPI.createOrderAfterPayment(orderPayload);
      
      if (response.success) {
        // Navigate to orders page
        navigate('/orders', {
          state: {
            orderCreated: true,
            orderId: response.data.orderId
          }
        });
      } else {
        throw new Error(response.message || 'Failed to create order');
      }
    } catch (error) {
      console.error('Order creation error:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Order creation failed. Please contact support.';
      alert(errorMessage);
    }
  };

  const handlePaymentClose = () => {
    setShowPaymentGateway(false);
    navigate('/emergency/oxygen-cylinder');
  };

  if (!orderData || !orderId) {
    return (
      <div className="payment-page">
        <div className="container">
          <div className="error-message">
            <h2>Invalid Order</h2>
            <p>Please go back and create a new order.</p>
            <button onClick={() => navigate('/emergency/oxygen-cylinder')}>
              Go Back
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="payment-page">
      <div className="container">
        {/* Header */}
        <div className="payment-header">
          <button 
            onClick={() => navigate('/emergency/oxygen-cylinder')}
            className="back-button"
          >
            ← Back to Order
          </button>
          <h1>Complete Your Payment</h1>
          <p>Secure payment for your oxygen cylinder subscription</p>
        </div>

        <div className="payment-content">
          {/* Order Summary */}
          <div className="order-summary-section">
            <h2>Order Summary</h2>
            <div className="summary-card">
              <div className="summary-row">
                <span>Plan:</span>
                <span>{orderData.plan.name}</span>
              </div>
              <div className="summary-row">
                <span>Duration:</span>
                <span>{orderData.plan.duration}</span>
              </div>
              <div className="summary-row">
                <span>Delivery:</span>
                <span>{orderData.delivery.name}</span>
              </div>
              <div className="summary-row">
                <span>Base Price:</span>
                <span>৳{(orderData.deliveryType === 'urgent' ? 
                  orderData.plan.urgentPrice : 
                  orderData.plan.price
                ).toLocaleString()}</span>
              </div>
              {orderData.delivery.price > 0 && (
                <div className="summary-row">
                  <span>Delivery Fee:</span>
                  <span>৳{orderData.delivery.price}</span>
                </div>
              )}
              <div className="summary-row total">
                <span>Total Amount:</span>
                <span>৳{orderData.totalAmount.toLocaleString()}</span>
              </div>
            </div>
          </div>

          {/* Payment Gateway */}
          {showPaymentGateway && (
            <PaymentGateway
              open={showPaymentGateway}
              onClose={handlePaymentClose}
              amount={orderData.totalAmount}
              paymentType="oxygen-cylinder"
              orderId={orderId}
              onSuccess={handlePaymentSuccess}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default OxygenCylinderPayment;